import React, { useMemo } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import RefreshButton from "components/RefreshButton";
import SLMPropertyIntegrationsList from "containers/slm/shared/SLMPropertyIntegrationsList";
import SLMPropertyIntegrationsModal from "containers/slm/shared/SLMPropertyIntegrationsModal";
import SLMTitleDetailsList from "containers/slm/shared/SLMTitleDetailsList";
import SLMTitleDetailsModal from "containers/slm/shared/SLMTitleDetailsModal";
import SLMVolumeFoliosList from "containers/slm/shared/SLMVolumeFoliosList";
import SLMVolumeFoliosModal from "containers/slm/shared/SLMVolumeFoliosModal";
import "containers/slm/lease/SLMLeaseDetail.css";
import { createElementIdFactory } from "utils/elementIds";

export default function SLMPropertyAddressPanel({
  colProps = { xs: 12, md: 6, xl: 3 },
  topCardId,
  colinId,
  pimsId,
  addressId,
  addressHardSequence,
  buildingName,
  streetAddressFull,
  suburb,
  stateCode,
  postcode,
  editAddressTo,
  editAddressButtonId,
  unitNumber,
  unitNumberInputId = "unit_number",
  onUnitNumberChange,
  onUnitNumberBlur,
  onAddressMetaChange,
  onAddressMetaBlur,
  titleDetails,
  onTitleDetailsChange,
  onCoreLogicEdit,
  onCoreLogicRefresh,
  onCoreLogicRefreshTimeout,
  coreLogicRefreshTimeout = 30000,
  coreLogicEditButtonId,
  coreLogicRefreshButtonId,
  showCoreLogicActions = true,
  coreLogicFilter,
  coreLogicIsAuthenticated,
  coreLogicData,
  coreLogicUpdateTableState,
  coreLogicSaveRecord,
  coreLogicDeleteRecord,
  coreLogicAcceptIntegration,
  coreLogicRejectIntegration,
  coreLogicContext,
  coreLogicShowModal = false,
  coreLogicModalOnClose,
  coreLogicModalProps,
  coreLogicListContent,
  coreLogicModalContent,
  parcelsSectionProps,
  volumeFoliosSectionProps,
  renderCoreLogicContent,
  renderCoreLogicSection,
  renderParcelsSection,
  renderVolumeFoliosSection,
  createId: createIdProp,
}) {
  const handleAddressMetaChange = onAddressMetaChange || (() => {});
  const handleAddressMetaBlur = onAddressMetaBlur || (() => {});
  const createId = useMemo(() => createIdProp || createElementIdFactory("slm-property-address-panel"), [createIdProp]);
  const hasSharedCoreLogicContent = !!coreLogicFilter && !!coreLogicContext;
  const hasParcelsSection = !!parcelsSectionProps || !!renderParcelsSection;
  const hasVolumeFoliosSection = !!volumeFoliosSectionProps || !!renderVolumeFoliosSection;

  return (
    <Col {...colProps} id={createId("container")}>
      <Form.Group as={Row} className="mb-2 align-items-center">
        <Form.Label id={createId("field", "colin-id", "label")} as={Col} className="text-right" column sm={6} md={6} lg={4}>
          CoLin ID
        </Form.Label>
        <Col style={{ paddingRight: 5 }}>
          <Form.Control id={createId("field", "colin-id", "input")} size="sm" disabled readOnly value={colinId || ""} type="text" className="slm-compact-id-input slm-compact-id-input-pr-5" title={colinId || ""} />
        </Col>
        <Form.Label id={createId("field", "pims-id", "label")} as={Col} column lg={2} md={4} sm={4} className="text-right mt-2 mt-lg-0" style={{ paddingRight: 1, paddingLeft: 1 }}>
          PIMs ID
        </Form.Label>
        <Col lg={3} md={3} sm={4} style={{ paddingLeft: 5 }}>
          <Form.Control id={createId("field", "pims-id", "input")} size="sm" disabled readOnly value={pimsId || ""} type="text" className="slm-compact-id-input" title={pimsId || ""} />
        </Col>
      </Form.Group>

      <Card id={topCardId} className="mb-2">
        <Card.Header>Property Address</Card.Header>
        <Card.Body>
          <Form.Group as={Row} className="mb-2 align-items-center">
            <Form.Label id={createId("field", "address-colin-id", "label")} as={Col} className="text-right" column sm={6} md={6} lg={4}>
              CoLin ID
            </Form.Label>
            <Col>
              <Form.Control size="sm" disabled readOnly onChange={handleAddressMetaChange} value={addressId || ""} type="text" id={createId("field", "address-colin-id", "input")} onBlur={handleAddressMetaBlur} className="slm-compact-id-input" title={addressId || ""} />
            </Col>

            <Form.Label id={createId("field", "address-pims-id", "label")} as={Col} column lg={2} md={4} sm={4} className="text-right mt-2 mt-lg-0" style={{ paddingRight: 1, paddingLeft: 1 }}>
              PIMs ID
            </Form.Label>
            <Col>
              <Form.Control size="sm" disabled readOnly onChange={handleAddressMetaChange} value={addressHardSequence || ""} type="text" id={createId("field", "address-pims-id", "input")} onBlur={handleAddressMetaBlur} className="slm-compact-id-input" title={addressHardSequence || ""} />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="building_name">
            <Form.Label id={createId("field", "building-name", "label")} as={Col} className="text-right" column sm={6} md={6} lg={4}>
              Building Name
            </Form.Label>
            <Col>
              <Form.Control id={createId("field", "building-name", "input")} size="sm" onChange={handleAddressMetaChange} value={buildingName || ""} type="text" readOnly className="slm-compact-input" />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label id={createId("field", "street-address", "label")} as={Col} className="text-right" column sm={6} md={6} lg={4}>
              Street Address
            </Form.Label>
            <Col>
              <Form.Control size="sm" onChange={handleAddressMetaChange} value={streetAddressFull || ""} type="text" id={createId("field", "street-address", "input")} readOnly className="slm-compact-input" />
            </Col>
          </Form.Group>

          <Form.Group as={Row}>
            <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}></Form.Label>
            <Col>
              <Form.Control size="sm" onChange={handleAddressMetaChange} value={(suburb || "") + " " + (stateCode || "") + " " + (postcode || "")} as="input" type="text" id={createId("field", "suburb-state-postcode", "input")} readOnly className="slm-compact-input" />
            </Col>
          </Form.Group>

          <Row className="mb-2">
            <Col column sm={6} md={6} lg={4}></Col>
            <Col className="text-right">
              <LinkContainer to={editAddressTo}>
                <Button id={editAddressButtonId} variant="primary" type="button" size="m" className="slm-compact-action-btn">
                  Edit Address
                </Button>
              </LinkContainer>{" "}
            </Col>
          </Row>

          <Form.Group as={Row} className="mt-2">
            <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
              Unit Number
            </Form.Label>
            <Col>
              <Form.Control size="sm" id={unitNumberInputId || createId("field", "unit-number", "input")} name="unit_number" onChange={onUnitNumberChange} onBlur={onUnitNumberBlur} value={unitNumber || ""} type="text" as="input" className="slm-compact-input" />
            </Col>
          </Form.Group>

          {renderCoreLogicSection ? (
            renderCoreLogicSection()
          ) : showCoreLogicActions || hasSharedCoreLogicContent || coreLogicListContent || coreLogicModalContent || renderCoreLogicContent ? (
            <Form.Group as={Row} controlId="property_integrations">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                CoreLogic Equivalent Properties
              </Form.Label>
              <Col>
                {showCoreLogicActions ? (
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <Button onClick={onCoreLogicEdit} size="m" variant="secondary" type="button" id={coreLogicEditButtonId} className="slm-compact-action-btn">
                      Edit
                    </Button>
                    <RefreshButton
                      onClick={onCoreLogicRefresh}
                      onTimeout={onCoreLogicRefreshTimeout}
                      timeout={coreLogicRefreshTimeout}
                      size="m"
                      className="slm-compact-action-btn"
                      variant="secondary"
                      type="button"
                      id={coreLogicRefreshButtonId}
                    />
                  </div>
                ) : null}
                {renderCoreLogicContent ? (
                  renderCoreLogicContent()
                ) : coreLogicListContent ? (
                  coreLogicListContent
                ) : hasSharedCoreLogicContent ? (
                  <SLMPropertyIntegrationsList
                    filter={coreLogicFilter}
                    isAuthenticated={coreLogicIsAuthenticated}
                    controlType="list"
                    data={coreLogicData}
                    updateTableState={coreLogicUpdateTableState}
                    saveRecord={coreLogicSaveRecord}
                    deleteRecord={coreLogicDeleteRecord}
                    acceptIntegration={coreLogicAcceptIntegration}
                    rejectIntegration={coreLogicRejectIntegration}
                    ctx={coreLogicContext}
                  />
                ) : null}
                {coreLogicModalContent
                  ? coreLogicModalContent
                  : hasSharedCoreLogicContent && coreLogicShowModal
                    ? (
                        <SLMPropertyIntegrationsModal
                          show={coreLogicShowModal}
                          onClose={coreLogicModalOnClose}
                          filter={coreLogicFilter}
                          isAuthenticated={coreLogicIsAuthenticated}
                          updateTableState={coreLogicUpdateTableState}
                          saveRecord={coreLogicSaveRecord}
                          deleteRecord={coreLogicDeleteRecord}
                          acceptIntegration={coreLogicAcceptIntegration}
                          rejectIntegration={coreLogicRejectIntegration}
                          ctx={coreLogicContext}
                          {...(coreLogicModalProps || {})}
                        />
                      )
                    : null}
              </Col>
            </Form.Group>
          ) : null}
        </Card.Body>
      </Card>

      <Card className="mb-2">
        <Card.Body>
          <Form.Group as={Row} controlId="title_details">
            <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
              Title Details
            </Form.Label>
            <Col>
              <Form.Control size="sm" onChange={onTitleDetailsChange || handleAddressMetaChange} value={titleDetails || ""} as="textarea" type="text" rows="5" disabled={true} />
            </Col>
          </Form.Group>

          {hasParcelsSection ? (
            renderParcelsSection ? (
              renderParcelsSection()
            ) : (
              <Form.Group as={Row} controlId="parcels">
                <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                  Parcels
                  <br />
                  (Top 3 - click Edit to view all)
                </Form.Label>
                <Col>
                  <Button onClick={parcelsSectionProps.onEdit} size="m" className="slm-compact-action-btn" variant="secondary" type="button" id={parcelsSectionProps.editButtonId} disabled={parcelsSectionProps.disabled ? true : false}>
                    Edit
                  </Button>
                  <SLMTitleDetailsList
                    filter={parcelsSectionProps.filter}
                    isAuthenticated={parcelsSectionProps.isAuthenticated}
                    controlType="list"
                    data={parcelsSectionProps.data}
                    dataFetch={parcelsSectionProps.dataFetch}
                    updateTableState={parcelsSectionProps.updateTableState}
                    saveRecord={parcelsSectionProps.saveRecord}
                    context={parcelsSectionProps.context}
                    stateCode={parcelsSectionProps.stateCode}
                    listAmount={parcelsSectionProps.listAmount || "3"}
                  />

                  {parcelsSectionProps.showModal ? (
                    <SLMTitleDetailsModal
                      show={parcelsSectionProps.showModal}
                      onClose={parcelsSectionProps.onClose}
                      filter={parcelsSectionProps.filter}
                      isAuthenticated={parcelsSectionProps.isAuthenticated}
                      toggleTitleDetailsModal={parcelsSectionProps.onClose}
                      user={parcelsSectionProps.user}
                      data={parcelsSectionProps.data}
                      dataFetch={parcelsSectionProps.dataFetch}
                      updateTableState={parcelsSectionProps.updateTableState}
                      saveRecord={parcelsSectionProps.saveRecord}
                      deleteRecord={parcelsSectionProps.deleteRecord}
                      validateRelatedRecord={parcelsSectionProps.validateRelatedRecord}
                      context={parcelsSectionProps.context}
                      stateCode={parcelsSectionProps.stateCode}
                      {...(parcelsSectionProps.modalProps || {})}
                    />
                  ) : null}
                </Col>
              </Form.Group>
            )
          ) : null}

          {hasVolumeFoliosSection ? (
            renderVolumeFoliosSection ? (
              renderVolumeFoliosSection()
            ) : (
              <Form.Group as={Row} controlId="volume_folios">
                <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                  Volume Folios
                  <br />
                  (Top 3 - click Edit to view all)
                </Form.Label>
                <Col>
                  <Button onClick={volumeFoliosSectionProps.onEdit} size="m" className="slm-compact-action-btn" variant="secondary" type="button" id={volumeFoliosSectionProps.editButtonId} disabled={volumeFoliosSectionProps.disabled ? true : false}>
                    Edit
                  </Button>
                  <SLMVolumeFoliosList
                    filter={volumeFoliosSectionProps.filter}
                    isAuthenticated={volumeFoliosSectionProps.isAuthenticated}
                    controlType="list"
                    data={volumeFoliosSectionProps.data}
                    dataFetch={volumeFoliosSectionProps.dataFetch}
                    updateTableState={volumeFoliosSectionProps.updateTableState}
                    saveRecord={volumeFoliosSectionProps.saveRecord}
                    deleteRecord={volumeFoliosSectionProps.deleteRecord}
                    context={volumeFoliosSectionProps.context}
                    listAmount={volumeFoliosSectionProps.listAmount || "3"}
                  />

                  {volumeFoliosSectionProps.showModal ? (
                    <SLMVolumeFoliosModal
                      show={volumeFoliosSectionProps.showModal}
                      onClose={volumeFoliosSectionProps.onClose}
                      filter={volumeFoliosSectionProps.filter}
                      isAuthenticated={volumeFoliosSectionProps.isAuthenticated}
                      toggleVolumeFoliosModal={volumeFoliosSectionProps.onClose}
                      user={volumeFoliosSectionProps.user}
                      data={volumeFoliosSectionProps.data}
                      dataFetch={volumeFoliosSectionProps.dataFetch}
                      updateTableState={volumeFoliosSectionProps.updateTableState}
                      saveRecord={volumeFoliosSectionProps.saveRecord}
                      deleteRecord={volumeFoliosSectionProps.deleteRecord}
                      validateRelatedRecord={volumeFoliosSectionProps.validateRelatedRecord}
                      context={volumeFoliosSectionProps.context}
                      {...(volumeFoliosSectionProps.modalProps || {})}
                    />
                  ) : null}
                </Col>
              </Form.Group>
            )
          ) : null}
        </Card.Body>
      </Card>
    </Col>
  );
}
